import React, { useState, useEffect, useMemo, useRef } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Popover, Row, Col, Tag, Input } from "antd";
import {
	ProductKeyValuePair,
	ProductMetaInfo,
	ProductTypes,
	SortOptions,
	SortingOptions,
	FilterTagData,
	FilterTagType
} from "@/Contracts/Products";
import { ProductSource } from "@/Contracts/Products";
import "./UnifiedItemFiltersMenu.less";
import {
	SupplierRequest,
	SupplierResponse,
	ZomentumPSAsArray,
	zomentumPSALabel
} from "@/Contracts";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import ZButton from "@atoms/ZButton";
import ZSelect from "@atoms/ZSelect";
import { EIcons } from "@atoms/ZIcon/interface";
import { ZMenu, ZSpin } from "@molecules";
import { ZCheckbox, ZCheckboxGroup } from "@atoms";
import { IOptionProps } from "@atoms/ZSelect/interface";
import debounce from "lodash/debounce";
import { capitalizeAfterUnderscore } from "@/Utils";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { useAddOrEditSupplier } from "@/Hooks/Supplier/useAddOrEditSupplier";
import { searchSupplier } from "@/Services/Settings";

export enum UnifiedFilterOptions {
	Manufacturers = "Manufacturers",
	Categories = "Categories",
	ProductType = "ProductType",
	Supplier = "Supplier",
	SyncSource = "SyncSource"
}

/**
 * Helper function to check if a category ID belongs to icecat categories
 */
const isIcecatCategory = (
	categoryId: string,
	metaInfo: ProductMetaInfo
): boolean => {
	return !!metaInfo.icecatCategories.find(
		(c: ProductKeyValuePair) => c.value === categoryId
	);
};

/**
 * Helper function to separate category IDs into regular and icecat categories
 */
const separateCategoryIds = (
	categoryIds: string[],
	metaInfo: ProductMetaInfo
): { regular: string[]; icecat: string[] } => {
	const regular: string[] = [];
	const icecat: string[] = [];

	categoryIds.forEach(id => {
		if (isIcecatCategory(id, metaInfo)) {
			icecat.push(id);
		} else {
			regular.push(id);
		}
	});

	return { regular, icecat };
};

export interface UnifiedItemFiltersMenuProps {
	readonly metaInfo: ProductMetaInfo;
	readonly selectedManufacturers: string[];
	readonly selectedCategories: string[];
	readonly selectedIcecatCategories: string[];
	readonly selectedProductTypes: string[];
	readonly selectedSuppliers: string[];
	readonly selectedSyncSources: string[];
	readonly excludeOutOfStock: boolean;
	readonly filterByConnectedDistributors: boolean;
	readonly excludeDropShipWH: boolean;
	readonly sortBy?: SortOptions;
	readonly onApply: (
		manufacturers: string[],
		categories: string[],
		icecatCategories: string[],
		productTypes: string[],
		suppliers: string[],
		syncSources: string[],
		excludeOutOfStock: boolean,
		filterByConnectedDistributors: boolean,
		excludeDropShipWH: boolean
	) => void;
	readonly onSortChange?: (value: SortOptions) => void;
}

const FilterTags = (props: {
	tagData: FilterTagData[];
	onTagClose: (type: FilterTagType, filterId: string) => void;
}) => {
	const containerRef = useRef<HTMLDivElement>(null);

	const filterTags = useMemo(() => {
		const noOfTags = 10;
		return props.tagData.length > noOfTags
			? {
					toShow: props.tagData.slice(0, noOfTags),
					others: props.tagData.slice(noOfTags, props.tagData.length)
			  }
			: { toShow: props.tagData, others: [] };
	}, [props.tagData]);

	const constructTagElements = (tagData: FilterTagData[]) => {
		return tagData.map(filterTag => {
			const key = `${filterTag.id}:${filterTag.type}`;
			const toDisplay = (
				<span className="item-filter-tag-data">{filterTag.name}</span>
			);
			return (
				<Tag
					key={key}
					closable={true}
					onClose={() =>
						props.onTagClose(filterTag.type, filterTag.id)
					}
					data-testid="filter-tag"
				>
					{toDisplay}
				</Tag>
			);
		});
	};

	return (
		<div ref={containerRef}>
			<Col span={24} className="item-filter-tag">
				{constructTagElements(filterTags.toShow)}
				{!!filterTags?.others?.length && (
					<Popover
						content={
							<Row>{constructTagElements(filterTags.others)}</Row>
						}
						trigger="click"
					>
						<span className="filter-show-more-button">
							{`${filterTags.others.length} more`}
						</span>
					</Popover>
				)}
			</Col>
		</div>
	);
};

const UnifiedItemFiltersMenu: React.FC<UnifiedItemFiltersMenuProps> = props => {
	const [visible, setVisible] = useState(false);

	const [selectedOption, setSelectedOption] = useState(
		UnifiedFilterOptions.Manufacturers
	);

	const [selectedManufacturers, setSelectedManufacturers] = useState(
		props.selectedManufacturers
	);
	const [selectedCategories, setSelectedCategories] = useState(
		props.selectedCategories
	);
	const [selectedIcecatCategories, setSelectedIcecatCategories] = useState(
		props.selectedIcecatCategories
	);
	const [selectedProductTypes, setSelectedProductTypes] = useState(
		props.selectedProductTypes
	);
	const [selectedSuppliers, setSelectedSuppliers] = useState<IOptionProps[]>(
		[]
	);
	const [selectedSyncSources, setSelectedSyncSources] = useState(
		props.selectedSyncSources
	);
	const [excludeOutOfStock, setExcludeOutOfStock] = useState(
		props.excludeOutOfStock
	);
	const [filterByConnectedDistributors, setFilterByConnectedDistributors] =
		useState(props.filterByConnectedDistributors);
	const [excludeDropShipWH, setExcludeDropShipWH] = useState(
		props.excludeDropShipWH
	);

	const [supplierOptions, setSupplierOptions] = useState<IOptionProps[]>([]);
	const [filterTags, setFilterTags] = useState<FilterTagData[]>([]);
	const [manufacturerSearchQuery, setManufacturerSearchQuery] = useState("");
	const [categorySearchQuery, setCategorySearchQuery] = useState("");
	const [productTypeSearchQuery, setProductTypeSearchQuery] = useState("");
	const [supplierSearchQuery, setSupplierSearchQuery] = useState("");
	const [syncSourceSearchQuery, setSyncSourceSearchQuery] = useState("");

	const { getSuppliers, getSupplierById } = useAddOrEditSupplier();
	const isIcecatIntegrationEnabled = useFeatureFlag("icecat_integration");
	const isAmazonDistributorEnabled = useFeatureFlag("amazon_distributor");

	const fetchSelectedSuppliersById = async () => {
		const selectedSupplierIds = selectedSuppliers.map(
			option => option.key
		) as string[];

		const response = await getSupplierById(selectedSupplierIds);
		const options = response?.data.map(option => ({
			name: option.name,
			value: option.id
		}));

		setSupplierOptions(options ?? []);
	};

	const fetchSuppliers = debounce(async (value?: string) => {
		try {
			let response: SupplierResponse;
			if (value) {
				const supplierRequest: SupplierRequest = {
					limit: 50,
					keywords: value ?? null
				};

				response = await searchSupplier(supplierRequest);
			} else {
				response = (await getSuppliers()) as SupplierResponse;
			}

			const options: IOptionProps[] = response.data.map(option => ({
				name: option.name,
				value: option.id
			}));

			setSupplierOptions(options);
		} catch (error) {
			console.error(error);
		}
	}, 300);

	const handleVisibleChange = (visible: boolean) => {
		setVisible(visible);

		if (visible) {
			setSelectedManufacturers(props.selectedManufacturers);
			setSelectedCategories(props.selectedCategories);
			setSelectedIcecatCategories(props.selectedIcecatCategories);
			setSelectedProductTypes(props.selectedProductTypes);
			setSelectedSyncSources(props.selectedSyncSources);
			setExcludeOutOfStock(props.excludeOutOfStock);
			setFilterByConnectedDistributors(
				props.filterByConnectedDistributors
			);
			setExcludeDropShipWH(props.excludeDropShipWH);
			setSelectedOption(UnifiedFilterOptions.Manufacturers);
			fetchSuppliers();
		}
	};

	const onApply = () => {
		setVisible(false);
		const supplierIds = selectedSuppliers.map(s => s.value as string);
		props.onApply(
			selectedManufacturers,
			selectedCategories,
			selectedIcecatCategories,
			selectedProductTypes,
			supplierIds,
			selectedSyncSources,
			excludeOutOfStock,
			filterByConnectedDistributors,
			excludeDropShipWH
		);
	};

	const handleRemoveAllFilters = () => {
		setSelectedManufacturers([]);
		setSelectedCategories([]);
		setSelectedIcecatCategories([]);
		setSelectedProductTypes([]);
		setSelectedSuppliers([]);
		setSelectedSyncSources([]);
		setExcludeOutOfStock(false);
		setFilterByConnectedDistributors(false);
		setExcludeDropShipWH(false);
	};

	const selectedFiltersCount = () => {
		return (
			selectedManufacturers.length +
			selectedCategories.length +
			selectedIcecatCategories.length +
			selectedProductTypes.length +
			selectedSuppliers.length +
			selectedSyncSources.length +
			(excludeOutOfStock ? 1 : 0) +
			(filterByConnectedDistributors ? 1 : 0) +
			(excludeDropShipWH ? 1 : 0)
		);
	};

	const onMainMenuChange = (key: UnifiedFilterOptions) => {
		setSelectedOption(key);
		setManufacturerSearchQuery("");
		setCategorySearchQuery("");
		setProductTypeSearchQuery("");
		setSupplierSearchQuery("");
		setSyncSourceSearchQuery("");
	};

	const manufacturerList = useMemo(() => {
		const selected = props.selectedManufacturers
			.map(id =>
				props.metaInfo.manufacturers.filter(item => item.value === id)
			)
			.reduce((a, b) => a.concat(b), []);
		const notSelected = props.metaInfo.manufacturers.filter(
			item => !selected.find(selected => selected.value === item.value)
		);
		const all = [...selected, ...notSelected];
		const withUpdatedCountInLabel = all.map(pair => {
			const newPair = new ProductKeyValuePair();
			newPair.label = `${pair.label} (${pair.count})`;
			newPair.value = pair.value;
			newPair.count = pair.count;
			return newPair;
		});
		return withUpdatedCountInLabel;
	}, [props.metaInfo.manufacturers, props.selectedManufacturers]);

	const filteredManufacturerList = useMemo(() => {
		if (manufacturerSearchQuery.trim()) {
			return manufacturerList.filter(manufacturer =>
				manufacturer.label
					.toLowerCase()
					.includes(manufacturerSearchQuery.toLowerCase())
			);
		}
		return manufacturerList;
	}, [manufacturerList, manufacturerSearchQuery]);

	const categoryList = useMemo(() => {
		// Merge both category types for display
		const allCategories = [
			...props.metaInfo.categories,
			...props.metaInfo.icecatCategories
		];

		const allSelectedIds = [
			...props.selectedCategories,
			...props.selectedIcecatCategories
		];

		const selected = allSelectedIds
			.map(id => allCategories.filter(item => item.value === id))
			.reduce((a, b) => a.concat(b), []);
		const notSelected = allCategories.filter(
			item => !selected.find(selected => selected.value === item.value)
		);
		const all = [...selected, ...notSelected];
		const withUpdatedCountInLabel = all.map(pair => {
			const newPair = new ProductKeyValuePair();
			newPair.label = `${pair.label} (${pair.count})`;
			newPair.value = pair.value;
			newPair.count = pair.count;
			return newPair;
		});
		return withUpdatedCountInLabel;
	}, [
		props.metaInfo.categories,
		props.metaInfo.icecatCategories,
		props.selectedCategories,
		props.selectedIcecatCategories
	]);

	const filteredCategoryList = useMemo(() => {
		if (categorySearchQuery.trim()) {
			return categoryList.filter(category =>
				category.label
					.toLowerCase()
					.includes(categorySearchQuery.toLowerCase())
			);
		}
		return categoryList;
	}, [categoryList, categorySearchQuery]);

	const productTypeList = useMemo(() => {
		const selected = props.selectedProductTypes
			.map(type => ProductTypes.find(pt => pt === type))
			.filter(Boolean) as string[];

		const notSelected = ProductTypes.filter(
			type => !selected.includes(type)
		);

		const all = [...selected, ...notSelected];

		return all.map(type => {
			const pair = new ProductKeyValuePair();
			pair.label = capitalizeFirstLetter(type);
			pair.value = type;
			return pair;
		});
	}, [props.selectedProductTypes]);

	const filteredProductTypeList = useMemo(() => {
		if (productTypeSearchQuery.trim()) {
			return productTypeList.filter(productType =>
				productType.label
					.toLowerCase()
					.includes(productTypeSearchQuery.toLowerCase())
			);
		}
		return productTypeList;
	}, [productTypeList, productTypeSearchQuery]);

	const supplierList = useMemo(() => {
		const selectedSupplierIds = props.selectedSuppliers;

		const selected = supplierOptions.filter(supplier =>
			selectedSupplierIds.includes(supplier.value as string)
		);

		const notSelected = supplierOptions.filter(
			supplier => !selectedSupplierIds.includes(supplier.value as string)
		);

		const all = [...selected, ...notSelected];

		return all.map(supplier => {
			const pair = new ProductKeyValuePair();
			pair.label = supplier.name as string;
			pair.value = supplier.value as string;
			return pair;
		});
	}, [supplierOptions, props.selectedSuppliers]);

	const filteredSupplierList = useMemo(() => {
		if (supplierSearchQuery.trim()) {
			return supplierList.filter(supplier =>
				supplier.label
					.toLowerCase()
					.includes(supplierSearchQuery.toLowerCase())
			);
		}
		return supplierList;
	}, [supplierList, supplierSearchQuery]);

	const syncSourceArray = useMemo(() => {
		return [
			...ZomentumPSAsArray,
			"dell_quote_import",
			"data_import",
			...(isIcecatIntegrationEnabled
				? [ProductSource.Icecat]
				: [ProductSource.Etilize, ProductSource.Icecat]),
			"zapier",
			...(isAmazonDistributorEnabled ? [ProductSource.Amazon] : [])
		];
	}, [isIcecatIntegrationEnabled, isAmazonDistributorEnabled]);

	const syncSourceList = useMemo(() => {
		const selected = props.selectedSyncSources
			.map(source =>
				syncSourceArray.find(s => {
					if (source === "infusionsoft" && s === "infusion_soft")
						return true;
					return s === source;
				})
			)
			.filter(Boolean) as string[];

		const notSelected = syncSourceArray.filter(source => {
			if (source === "infusion_soft") {
				return !props.selectedSyncSources.includes("infusionsoft");
			}
			return !props.selectedSyncSources.includes(source);
		});

		const all = [...selected, ...notSelected];

		return all.map(source => {
			const pair = new ProductKeyValuePair();

			pair.value = source === "infusion_soft" ? "infusionsoft" : source;

			if (source === ProductSource.Icecat && isIcecatIntegrationEnabled) {
				pair.label = "Global Catalogue";
			} else if (zomentumPSALabel[source]) {
				pair.label = zomentumPSALabel[source];
			} else {
				pair.label = capitalizeAfterUnderscore(source);
			}

			return pair;
		});
	}, [
		syncSourceArray,
		props.selectedSyncSources,
		isIcecatIntegrationEnabled
	]);

	const filteredSyncSourceList = useMemo(() => {
		if (syncSourceSearchQuery.trim()) {
			return syncSourceList.filter(syncSource =>
				syncSource.label
					.toLowerCase()
					.includes(syncSourceSearchQuery.toLowerCase())
			);
		}
		return syncSourceList;
	}, [syncSourceList, syncSourceSearchQuery]);

	const filterCount = selectedFiltersCount();

	useEffect(() => {
		if (!!!selectedSuppliers.length) {
			const supplierIds = props.selectedSuppliers;
			if (supplierIds.length > 0) {
				fetchSelectedSuppliersById();
			}
		}
	}, [props.selectedSuppliers]);

	useEffect(() => {
		if (
			selectedOption === UnifiedFilterOptions.Supplier &&
			supplierOptions.length === 0
		) {
			fetchSuppliers();
		}
	}, [selectedOption]);

	useEffect(() => {
		const constructTags = (
			selectedFilters: string[],
			meta: ProductKeyValuePair[],
			type: FilterTagType
		) => {
			return selectedFilters
				.map(filter => {
					const selected = meta.find(item => filter === item.value);
					if (selected) {
						const tag = new FilterTagData();
						tag.id = selected.value;
						tag.name = selected.label;
						tag.type = type;
						tag.isSelfFilter = true;
						return tag;
					}
					return null;
				})
				.filter(Boolean) as FilterTagData[];
		};

		let tags: FilterTagData[] = [];

		tags = tags.concat(
			constructTags(
				props.selectedManufacturers,
				props.metaInfo.manufacturers,
				FilterTagType.SelfManufacturer
			)
		);

		tags = tags.concat(
			constructTags(
				props.selectedCategories,
				props.metaInfo.categories,
				FilterTagType.SelfCategory
			)
		);

		tags = tags.concat(
			constructTags(
				props.selectedIcecatCategories,
				props.metaInfo.icecatCategories,
				FilterTagType.SelfCategory
			)
		);

		props.selectedProductTypes.forEach(type => {
			const tag = new FilterTagData();
			tag.id = type;
			tag.name = capitalizeFirstLetter(type);
			tag.type = FilterTagType.SelfProductType;
			tag.isSelfFilter = true;
			tags.push(tag);
		});

		props.selectedSuppliers.forEach(supplierId => {
			const supplierOption = selectedSuppliers.find(
				s => s.value === supplierId
			);
			if (supplierOption) {
				const tag = new FilterTagData();
				tag.id = supplierId;
				tag.name = String(supplierOption.name);
				tag.type = FilterTagType.SelfSupplier;
				tag.isSelfFilter = true;
				tags.push(tag);
			}
		});

		props.selectedSyncSources.forEach(source => {
			const tag = new FilterTagData();
			tag.id = source;
			tag.name =
				source === ProductSource.Icecat && isIcecatIntegrationEnabled
					? "Global Catalogue"
					: zomentumPSALabel[source] ||
					  capitalizeAfterUnderscore(source);
			tag.type = FilterTagType.SelfSyncSource;
			tag.isSelfFilter = true;
			tags.push(tag);
		});

		if (props.excludeOutOfStock) {
			const tag = new FilterTagData();
			tag.id = "exclude_out_of_stock";
			tag.name = "Exclude Out of Stock";
			tag.type = "ExcludeOutOfStock" as FilterTagType;
			tag.isSelfFilter = true;
			tags.push(tag);
		}

		if (props.filterByConnectedDistributors) {
			const tag = new FilterTagData();
			tag.id = "filter_by_connected_distributors";
			tag.name = "Limit to my distributors";
			tag.type = "LimitDistributors" as FilterTagType;
			tag.isSelfFilter = true;
			tags.push(tag);
		}

		if (props.excludeDropShipWH) {
			const tag = new FilterTagData();
			tag.id = "exclude_drop_ship_wh";
			tag.name = "Exclude DropShip WH";
			tag.type = "ExcludeDropShipWH" as FilterTagType;
			tag.isSelfFilter = true;
			tags.push(tag);
		}

		setFilterTags(tags);
	}, [
		props.selectedManufacturers,
		props.selectedCategories,
		props.selectedIcecatCategories,
		props.selectedProductTypes,
		props.selectedSuppliers,
		props.selectedSyncSources,
		props.excludeOutOfStock,
		props.filterByConnectedDistributors,
		props.excludeDropShipWH,
		props.metaInfo,
		selectedSuppliers,
		isIcecatIntegrationEnabled
	]);

	const sellPriceSortingOptions = SortingOptions.filter(
		option =>
			option.value === SortOptions.SellPriceLowToHigh ||
			option.value === SortOptions.SellPriceHighToLow
	);

	const onTagClose = (type: FilterTagType, filterId: string) => {
		let updatedManufacturers = props.selectedManufacturers;
		let updatedCategories = props.selectedCategories;
		let updatedIcecatCategories = props.selectedIcecatCategories;
		let updatedProductTypes = props.selectedProductTypes;
		let updatedSuppliers = props.selectedSuppliers;
		let updatedSyncSources = props.selectedSyncSources;
		let updatedExcludeOutOfStock = props.excludeOutOfStock;
		let updatedFilterByConnectedDistributors =
			props.filterByConnectedDistributors;
		let updatedExcludeDropShipWH = props.excludeDropShipWH;

		switch (type) {
			case FilterTagType.SelfManufacturer:
				updatedManufacturers = props.selectedManufacturers.filter(
					id => id !== filterId
				);
				break;

			case FilterTagType.SelfCategory:
				if (isIcecatCategory(filterId, props.metaInfo)) {
					updatedIcecatCategories =
						props.selectedIcecatCategories.filter(
							id => id !== filterId
						);
				} else {
					updatedCategories = props.selectedCategories.filter(
						id => id !== filterId
					);
				}
				break;

			case FilterTagType.SelfProductType:
				updatedProductTypes = props.selectedProductTypes.filter(
					id => id !== filterId
				);
				break;

			case FilterTagType.SelfSupplier:
				updatedSuppliers = props.selectedSuppliers.filter(
					id => id !== filterId
				);
				break;

			case FilterTagType.SelfSyncSource:
				updatedSyncSources = props.selectedSyncSources.filter(
					id => id !== filterId
				);
				break;

			case "ExcludeOutOfStock" as FilterTagType:
				updatedExcludeOutOfStock = false;
				break;

			case "LimitDistributors" as FilterTagType:
				updatedFilterByConnectedDistributors = false;
				break;

			case "ExcludeDropShipWH" as FilterTagType:
				updatedExcludeDropShipWH = false;
				break;
		}

		props.onApply(
			updatedManufacturers,
			updatedCategories,
			updatedIcecatCategories,
			updatedProductTypes,
			updatedSuppliers,
			updatedSyncSources,
			updatedExcludeOutOfStock,
			updatedFilterByConnectedDistributors,
			updatedExcludeDropShipWH
		);
	};

	return (
		<div className="unified-item-filters-menu">
			<Row>
				<Col span={5}>
					<Popover
						trigger="click"
						visible={visible}
						onVisibleChange={handleVisibleChange}
						overlayClassName="unified-item-filter-popover"
						placement="bottomLeft"
						content={
							<ZSpin spinning={false}>
								<Row className="unified-item-filter">
									<Row className="main-menu">
										<ZMenu
											onClick={(event: any) =>
												onMainMenuChange(
													event.key as UnifiedFilterOptions
												)
											}
											selectedKeys={[
												selectedOption.toString()
											]}
										>
											<ZMenu.Item
												key={
													UnifiedFilterOptions.Manufacturers
												}
											>
												Manufacturers
											</ZMenu.Item>
											<ZMenu.Item
												key={
													UnifiedFilterOptions.Categories
												}
											>
												Categories
											</ZMenu.Item>
											<ZMenu.Item
												key={
													UnifiedFilterOptions.ProductType
												}
											>
												Product Type
											</ZMenu.Item>
											<ZMenu.Item
												key={
													UnifiedFilterOptions.Supplier
												}
											>
												Supplier
											</ZMenu.Item>
											<ZMenu.Item
												key={
													UnifiedFilterOptions.SyncSource
												}
											>
												Sync Source
											</ZMenu.Item>
										</ZMenu>
										<div className="flex flex-col justify-end mb-2 mt-4 pl-4">
											<ZCheckbox
												checked={
													filterByConnectedDistributors
												}
												onChange={e =>
													setFilterByConnectedDistributors(
														e.target.checked
													)
												}
											>
												Limit results to my distributors
											</ZCheckbox>
											<ZCheckbox
												checked={excludeOutOfStock}
												onChange={e =>
													setExcludeOutOfStock(
														e.target.checked
													)
												}
											>
												Exclude Out of stock
											</ZCheckbox>
											<ZCheckbox
												checked={excludeDropShipWH}
												onChange={e =>
													setExcludeDropShipWH(
														e.target.checked
													)
												}
											>
												Exclude DropShip WH
											</ZCheckbox>
										</div>
									</Row>
									<Row className="sub-main-wrap">
										{selectedOption ===
										UnifiedFilterOptions.Manufacturers ? (
											<div style={{ width: "100%" }}>
												<div
													className="ml-6 mr-5"
													style={{
														position: "sticky",
														top: 0,
														backgroundColor: "#fff",
														zIndex: 10,
														paddingTop: "4px",
														paddingBottom: "8px"
													}}
												>
													<Input.Search
														placeholder="Search manufacturers..."
														allowClear
														value={
															manufacturerSearchQuery
														}
														onChange={e =>
															setManufacturerSearchQuery(
																e.target.value
															)
														}
														style={{
															width: "100%"
														}}
													/>
												</div>
												<div
													style={{
														maxHeight: "400px",
														overflowY: "auto",
														paddingRight: "4px",
														scrollBehavior: "smooth"
													}}
												>
													{filteredManufacturerList.length >
													0 ? (
														<ZCheckboxGroup
															spaceClassName="ml-6 mr-5"
															space="middle"
															vertical
															options={
																filteredManufacturerList
															}
															value={
																selectedManufacturers
															}
															onChange={e => {
																const stringValues =
																	e.map(a =>
																		a.toString()
																	);
																setSelectedManufacturers(
																	stringValues
																);
															}}
														/>
													) : (
														<div className="ml-6 mr-5 text-gray-500">
															No manufacturers
															found for "
															{
																manufacturerSearchQuery
															}
															"
														</div>
													)}
												</div>
											</div>
										) : selectedOption ===
										  UnifiedFilterOptions.Categories ? (
											<div style={{ width: "100%" }}>
												<div
													className="ml-6 mr-5"
													style={{
														position: "sticky",
														top: 0,
														backgroundColor: "#fff",
														zIndex: 10,
														paddingTop: "4px",
														paddingBottom: "8px"
													}}
												>
													<Input.Search
														placeholder="Search categories..."
														allowClear
														value={
															categorySearchQuery
														}
														onChange={e =>
															setCategorySearchQuery(
																e.target.value
															)
														}
														style={{
															width: "100%"
														}}
													/>
												</div>
												<div
													style={{
														maxHeight: "400px",
														overflowY: "auto",
														paddingRight: "4px",
														scrollBehavior: "smooth"
													}}
												>
													{filteredCategoryList.length >
													0 ? (
														<ZCheckboxGroup
															spaceClassName="ml-6 mr-5"
															space="middle"
															vertical
															options={
																filteredCategoryList
															}
															value={[
																...selectedCategories,
																...selectedIcecatCategories
															]}
															onChange={e => {
																const stringValues =
																	e.map(a =>
																		a.toString()
																	);

																const separated =
																	separateCategoryIds(
																		stringValues,
																		props.metaInfo
																	);

																setSelectedCategories(
																	separated.regular
																);
																setSelectedIcecatCategories(
																	separated.icecat
																);
															}}
														/>
													) : (
														<div className="ml-6 mr-5 text-gray-500">
															No categories found
															for "
															{
																categorySearchQuery
															}
															"
														</div>
													)}
												</div>
											</div>
										) : selectedOption ===
										  UnifiedFilterOptions.ProductType ? (
											<div style={{ width: "100%" }}>
												<div
													className="ml-6 mr-5"
													style={{
														position: "sticky",
														top: 0,
														backgroundColor: "#fff",
														zIndex: 10,
														paddingTop: "4px",
														paddingBottom: "8px"
													}}
												>
													<Input.Search
														placeholder="Search product types..."
														allowClear
														value={
															productTypeSearchQuery
														}
														onChange={e =>
															setProductTypeSearchQuery(
																e.target.value
															)
														}
														style={{
															width: "100%"
														}}
													/>
												</div>
												<div
													style={{
														maxHeight: "400px",
														overflowY: "auto",
														paddingRight: "4px",
														scrollBehavior: "smooth"
													}}
												>
													{filteredProductTypeList.length >
													0 ? (
														<ZCheckboxGroup
															spaceClassName="ml-6 mr-5"
															space="middle"
															vertical
															options={
																filteredProductTypeList
															}
															value={
																selectedProductTypes
															}
															onChange={e => {
																const stringValues =
																	e.map(a =>
																		a.toString()
																	);
																setSelectedProductTypes(
																	stringValues
																);
															}}
														/>
													) : (
														<div className="ml-6 mr-5 text-gray-500">
															No product types
															found for "
															{
																productTypeSearchQuery
															}
															"
														</div>
													)}
												</div>
											</div>
										) : selectedOption ===
										  UnifiedFilterOptions.Supplier ? (
											<div style={{ width: "100%" }}>
												<div
													className="ml-6 mr-5"
													style={{
														position: "sticky",
														top: 0,
														backgroundColor: "#fff",
														zIndex: 10,
														paddingTop: "4px",
														paddingBottom: "8px"
													}}
												>
													<Input.Search
														placeholder="Search suppliers..."
														allowClear
														value={
															supplierSearchQuery
														}
														onChange={e =>
															setSupplierSearchQuery(
																e.target.value
															)
														}
														style={{
															width: "100%"
														}}
													/>
												</div>
												<div
													style={{
														maxHeight: "400px",
														overflowY: "auto",
														paddingRight: "4px",
														scrollBehavior: "smooth"
													}}
												>
													{filteredSupplierList.length >
													0 ? (
														<ZCheckboxGroup
															spaceClassName="ml-6 mr-5"
															space="middle"
															vertical
															options={
																filteredSupplierList
															}
															value={selectedSuppliers.map(
																s =>
																	s.value as string
															)}
															onChange={e => {
																const stringValues =
																	e.map(a =>
																		a.toString()
																	);
																const selectedOptions =
																	supplierOptions.filter(
																		opt =>
																			stringValues.includes(
																				opt.value as string
																			)
																	);
																setSelectedSuppliers(
																	selectedOptions
																);
															}}
														/>
													) : (
														<div className="ml-6 mr-5 text-gray-500">
															No suppliers found
															for "
															{
																supplierSearchQuery
															}
															"
														</div>
													)}
												</div>
											</div>
										) : selectedOption ===
										  UnifiedFilterOptions.SyncSource ? (
											<div style={{ width: "100%" }}>
												<div
													className="ml-6 mr-5"
													style={{
														position: "sticky",
														top: 0,
														backgroundColor: "#fff",
														zIndex: 10,
														paddingTop: "4px",
														paddingBottom: "8px"
													}}
												>
													<Input.Search
														placeholder="Search sync sources..."
														allowClear
														value={
															syncSourceSearchQuery
														}
														onChange={e =>
															setSyncSourceSearchQuery(
																e.target.value
															)
														}
														style={{
															width: "100%"
														}}
													/>
												</div>
												<div
													style={{
														maxHeight: "400px",
														overflowY: "auto",
														paddingRight: "4px",
														scrollBehavior: "smooth"
													}}
												>
													{filteredSyncSourceList.length >
													0 ? (
														<ZCheckboxGroup
															spaceClassName="ml-6 mr-5"
															space="middle"
															vertical
															options={
																filteredSyncSourceList
															}
															value={
																selectedSyncSources
															}
															onChange={e => {
																const stringValues =
																	e.map(a =>
																		a.toString()
																	);
																setSelectedSyncSources(
																	stringValues
																);
															}}
														/>
													) : (
														<div className="ml-6 mr-5 text-gray-500">
															No sync sources
															found for "
															{
																syncSourceSearchQuery
															}
															"
														</div>
													)}
												</div>
											</div>
										) : null}
									</Row>
									<div className="footer-wrap">
										{filterCount ? (
											<span className="filters-count-placeholder">
												<span
													className="filters-count"
													data-testid="filter-count"
												>{`${filterCount} Filter(s) selected`}</span>
												<CloseOutlined
													onClick={
														handleRemoveAllFilters
													}
												/>
											</span>
										) : (
											<span></span>
										)}
										<span></span>
										<ZButton
											onClick={() =>
												handleVisibleChange(false)
											}
											type="default"
										>
											Cancel
										</ZButton>
										<ZButton
											onClick={onApply}
											type="primary"
										>
											Apply
										</ZButton>
									</div>
								</Row>
							</ZSpin>
						}
					>
						<ZButton
							iconName={EIcons.FILTER}
							type="primary"
							ghost
							size="small"
						>
							Add Filters
						</ZButton>
					</Popover>
				</Col>
				<Col span={14}>
					<FilterTags tagData={filterTags} onTagClose={onTagClose} />
				</Col>
				<Col span={5}>
					<ZSelect
						size="small"
						onChange={(value: SortOptions) =>
							props.onSortChange?.(value)
						}
						placeholder="Sort Products by"
						allowClear={true}
						value={props.sortBy}
						selectOptions={sellPriceSortingOptions}
						className="w-full float-right"
					/>
				</Col>
			</Row>
		</div>
	);
};

export default UnifiedItemFiltersMenu;
